import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';

import { AxiosError } from 'axios';
import { VerifyOtpCodePayload } from '../models/payload/auth';
import UpdateUserProfilePayload from '../models/payload/profile';
import { VerifyResponse } from '../models/response/auth';
import { IError, IErrorResponse } from '../models/response/error';
import { UserResponse } from '../models/response/profile';
import { logOut, verifyCode } from '../services/auth';
import { getProfile, updateProfile } from '../services/profile';
import setToken from '../utils/auth/setToken';
import cookieUtil from '../utils/cookieUtil';
// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';

declare global {
  interface Window {
    Intk?: any;
  }
}

interface AuthStateStructure {
  user: UserResponse | undefined;
  loading: boolean;
  errors: IError | undefined;
}

const initialState: AuthStateStructure = {
  user: undefined,
  loading: false,
  errors: undefined,
};

const getUserProfile = createAppAsyncThunk<UserResponse, string>('auth/getProfile', async (token?: string) => {
  const response = await getProfile(token);
  return response;
});

const verifyOtp = createAppAsyncThunk<VerifyResponse, VerifyOtpCodePayload, { rejectValue: IErrorResponse }>(
  'auth/verifyOtp',
  async (input, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    try {
      const isInBo = window.location.href.includes('/bo');
      const response = await verifyCode({
        userId: state.otp.userId ?? '',
        otp: input.otp,
        firstName: !state.otp.isRegistered ? input.firstName : null,
        lastName: !state.otp.isRegistered ? input.lastName : null,
      });

      if (response?.data?.token && response?.data?.refreshToken) {
        setToken(response?.data?.token, response?.data?.refreshToken);
        localStorage.setItem('token', response?.data?.token);
        localStorage.setItem('refreshToken', response?.data?.refreshToken);

        dispatch(getUserProfile(response.data.token))
          .unwrap()
          .then(() => {
            // if (window?.Intk && data) {
            //   window?.Intk('login', {
            //     userId: data.data?.id,
            //     firstName: data.data?.firstName,
            //     lastName: data.data?.lastName,
            //     phone: data.data?.phoneNumber ? data.data.phoneNumber.replace(/^0/, '+98') : null,
            //   });
            // }
          })
          .then(() => {
            if (isInBo) {
              window.location.href = '/bo';
            }
            // else {
            //   const urlParams = new URLSearchParams(window.location.search);
            //   const table = urlParams.get('table');
            //   window.location.href = table ? `?table=${table}` : '';
            // }
          });
      }

      return response;
    } catch (error) {
      return rejectWithValue((error as AxiosError).response?.data as IErrorResponse);
    }
  }
);

const autoLogin = createAppAsyncThunk<UserResponse, string>('auth/autoLogin', async (token?: string) => {
  const response = await getProfile(token);
  return response;
});

const logout = createAppAsyncThunk<void, void>('auth/logout', async () => {
  cookieUtil.removeCookie('token');
  cookieUtil.removeCookie('refreshToken');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.clear();

  // if (window?.Intk) {
  //   window?.Intk('logout');
  // }

  const isInBo = window.location.href.includes('/bo');

  if (isInBo) {
    await logOut();
    window.location.href = '/bo/auth/login';
  } else {
    const urlParams = new URLSearchParams(window.location.search);
    const table = urlParams.get('table');
    window.location.href = table ? `/?table=${table}` : '/';
  }
});

const updateUserInformation = createAppAsyncThunk<void, UpdateUserProfilePayload>(
  'auth/updateUserInformation',
  async (input) => {
    const response = await updateProfile(input);
    return response.data;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    removeErrors: (state) => ({ ...state, errors: undefined, loading: false }),
    removeUser: (state) => ({ ...state, errors: undefined, loading: false, user: undefined }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<AuthStateStructure>) => {
    builder.addCase(verifyOtp.pending, (state) => ({ ...state, errors: undefined, loading: true }));

    builder.addCase(verifyOtp.fulfilled, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(verifyOtp.rejected, (state, action) => ({
      ...state,
      errors: action?.payload?.data?.errors,
      loading: false,
      permissions: undefined,
    }));

    builder.addCase(getUserProfile.fulfilled, (state, action) => ({
      ...state,
      user: action.payload,
    }));

    builder.addCase(updateUserInformation.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(updateUserInformation.fulfilled, (state, action) => ({
      ...state,
      user: {
        ...state?.user,
        data: {
          ...state?.user?.data,
          firstName: action?.meta?.arg?.firstName,
          lastName: action?.meta?.arg?.lastName,
          email: action?.meta?.arg?.email,
        },
      },
      loading: false,
    }));

    builder.addCase(updateUserInformation.rejected, (state) => ({
      ...state,
      user: undefined,
      loading: false,
      permissions: undefined,
    }));

    builder.addCase(autoLogin.fulfilled, (state, action) => ({
      ...state,
      user: action.payload,
      load: false,
      loading: false,
    }));

    builder.addCase(autoLogin.rejected, (state) => ({
      ...state,
      user: undefined,
      loading: false,
      permissions: undefined,
    }));

    builder.addCase(logout.fulfilled, (state) => ({
      ...state,
      user: undefined,
      loading: false,
      permissions: undefined,
    }));
  },
});

export default authSlice;
export { autoLogin, logout, updateUserInformation, verifyOtp };
